import React, { useState, useEffect } from 'react';
import { Flower2, ChevronLeft, Plus, Calendar as CalendarIcon, Droplet, TrendingUp, BarChart3, Clock, Trash2, Edit2, X, Check, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import BottomNav from '../components/BottomNav';
import { cycleAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const CycleHistory = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [visualData, setVisualData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [newPeriod, setNewPeriod] = useState({
    periodStart: new Date().toISOString().split('T')[0],
    periodEnd: '',
    cycleLength: '',
    flowIntensity: 'medium',
    notes: ''
  });

  const fetchHistory = async () => {
    try {
      const response = await cycleAPI.getHistory(12);
      setHistory(response.data.history || []);
      setStats(response.data.stats || {});
      setVisualData(response.data.visualData || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger l\'historique',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleAddPeriod = async () => {
    try {
      await cycleAPI.recordPeriod(newPeriod);
      toast({
        title: 'Succès',
        description: 'Période enregistrée'
      });
      setShowAddDialog(false);
      setNewPeriod({
        periodStart: new Date().toISOString().split('T')[0],
        periodEnd: '',
        cycleLength: '',
        flowIntensity: 'medium',
        notes: ''
      });
      fetchHistory();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de l\'enregistrement',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateRecord = async (recordId, data) => {
    try {
      await cycleAPI.updateRecord(recordId, data);
      toast({
        title: 'Succès',
        description: 'Enregistrement mis à jour'
      });
      setEditingRecord(null);
      fetchHistory();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Supprimer cet enregistrement ?')) return;
    
    try {
      await cycleAPI.deleteRecord(recordId);
      toast({
        title: 'Supprimé',
        description: 'Enregistrement supprimé'
      });
      fetchHistory();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la suppression',
        variant: 'destructive'
      });
    }
  };

  const getFlowColor = (intensity) => {
    switch (intensity) {
      case 'light': return 'bg-red-300';
      case 'medium': return 'bg-red-500';
      case 'heavy': return 'bg-red-700';
      default: return 'bg-red-500';
    }
  };

  const getFlowLabel = (intensity) => {
    switch (intensity) {
      case 'light': return 'Léger';
      case 'medium': return 'Moyen';
      case 'heavy': return 'Abondant';
      default: return 'Moyen';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Calculate max cycle length for chart scaling
  const maxCycleLength = Math.max(...visualData.filter(d => d.cycleLength).map(d => d.cycleLength), 35);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/calendar')} className="p-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-pink-300" />
              <h1 className="text-xl font-bold text-white">Historique des cycles</h1>
            </div>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-pink-500 hover:bg-pink-600">
                <Plus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-purple-900 border-purple-700 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white text-xl flex items-center gap-2">
                  <Droplet className="w-6 h-6 text-red-400" />
                  Enregistrer une période
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-pink-200">Début des règles *</Label>
                  <Input
                    type="date"
                    value={newPeriod.periodStart}
                    onChange={(e) => setNewPeriod({...newPeriod, periodStart: e.target.value})}
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-pink-200">Fin des règles</Label>
                  <Input
                    type="date"
                    value={newPeriod.periodEnd}
                    onChange={(e) => setNewPeriod({...newPeriod, periodEnd: e.target.value})}
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-pink-200">Intensité du flux</Label>
                  <Select 
                    value={newPeriod.flowIntensity} 
                    onValueChange={(val) => setNewPeriod({...newPeriod, flowIntensity: val})}
                  >
                    <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Léger</SelectItem>
                      <SelectItem value="medium">Moyen</SelectItem>
                      <SelectItem value="heavy">Abondant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-pink-200">Notes (optionnel)</Label>
                  <Input
                    placeholder="Symptômes, remarques..."
                    value={newPeriod.notes}
                    onChange={(e) => setNewPeriod({...newPeriod, notes: e.target.value})}
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
                <Button onClick={handleAddPeriod} className="w-full bg-gradient-to-r from-pink-500 to-purple-500">
                  Enregistrer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Stats Overview */}
        {stats && stats.totalCyclesRecorded > 0 && (
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-pink-400" />
              Statistiques
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-pink-500/20 rounded-lg">
                <p className="text-2xl font-bold text-white">{stats.averageCycleLength || '--'}</p>
                <p className="text-pink-200 text-xs">Durée moyenne</p>
              </div>
              <div className="text-center p-3 bg-purple-500/20 rounded-lg">
                <p className="text-2xl font-bold text-white">{stats.totalCyclesRecorded}</p>
                <p className="text-pink-200 text-xs">Cycles enregistrés</p>
              </div>
              <div className="text-center p-3 bg-blue-500/20 rounded-lg">
                <p className="text-2xl font-bold text-white">
                  {stats.shortestCycle && stats.longestCycle ? `${stats.shortestCycle}-${stats.longestCycle}` : '--'}
                </p>
                <p className="text-pink-200 text-xs">Min - Max jours</p>
              </div>
              <div className="text-center p-3 rounded-lg" style={{
                backgroundColor: stats.isRegular ? 'rgba(34, 197, 94, 0.2)' : 'rgba(251, 191, 36, 0.2)'
              }}>
                <p className="text-2xl font-bold text-white">
                  {stats.isRegular === null ? '--' : stats.isRegular ? '✓' : '~'}
                </p>
                <p className="text-pink-200 text-xs">
                  {stats.isRegular === null ? 'Régularité' : stats.isRegular ? 'Régulier' : 'Irrégulier'}
                </p>
              </div>
            </div>
            {stats.cycleVariation !== null && (
              <p className="text-pink-200 text-xs mt-3 text-center">
                Variation: {stats.cycleVariation} jours entre le plus court et le plus long cycle
              </p>
            )}
          </Card>
        )}

        {/* Visual Chart */}
        {visualData.length > 0 && (
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-pink-400" />
              Évolution des cycles
            </h3>
            <div className="space-y-2">
              {visualData.slice(0, 8).reverse().map((data, index) => {
                const cycleLength = data.cycleLength || 28;
                const barWidth = (cycleLength / maxCycleLength) * 100;
                return (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-pink-200 text-xs w-16 shrink-0">
                      {formatDate(data.date).split(' ').slice(0, 2).join(' ')}
                    </span>
                    <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden relative">
                      <div 
                        className={`h-full ${getFlowColor(data.flowIntensity)} rounded-full transition-all duration-500`}
                        style={{ width: `${barWidth}%` }}
                      />
                      {data.cycleLength && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-white text-xs font-medium">
                          {data.cycleLength}j
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-4 mt-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-300" />
                <span className="text-pink-200">Léger</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-500" />
                <span className="text-pink-200">Moyen</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-700" />
                <span className="text-pink-200">Abondant</span>
              </div>
            </div>
          </Card>
        )}

        {/* History List */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-pink-400" />
            Historique détaillé
          </h3>
          
          {history.length === 0 ? (
            <div className="text-center py-8">
              <CalendarIcon className="w-12 h-12 text-pink-300/50 mx-auto mb-3" />
              <p className="text-pink-200">Aucun cycle enregistré</p>
              <p className="text-pink-200/60 text-sm mt-1">
                Commencez par ajouter votre première période
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((record) => (
                <div 
                  key={record.id}
                  className="p-3 bg-white/5 rounded-lg border border-white/10"
                >
                  {editingRecord === record.id ? (
                    <div className="space-y-2">
                      <Input
                        type="date"
                        defaultValue={record.periodEnd}
                        onChange={(e) => record._editPeriodEnd = e.target.value}
                        className="bg-white/10 border-white/20 text-white text-sm"
                        placeholder="Fin des règles"
                      />
                      <Select 
                        defaultValue={record.flowIntensity || 'medium'}
                        onValueChange={(val) => record._editFlow = val}
                      >
                        <SelectTrigger className="bg-white/10 border-white/20 text-white text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="light">Léger</SelectItem>
                          <SelectItem value="medium">Moyen</SelectItem>
                          <SelectItem value="heavy">Abondant</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setEditingRecord(null)}
                          className="text-white hover:bg-white/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => handleUpdateRecord(record.id, {
                            periodEnd: record._editPeriodEnd || record.periodEnd,
                            flowIntensity: record._editFlow || record.flowIntensity
                          })}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Droplet className={`w-4 h-4 ${
                            record.flowIntensity === 'heavy' ? 'text-red-400' : 
                            record.flowIntensity === 'light' ? 'text-red-200' : 'text-red-300'
                          }`} />
                          <span className="text-white font-medium">
                            {formatDate(record.periodStart)}
                          </span>
                          {record.periodEnd && (
                            <span className="text-pink-200 text-sm">
                              → {formatDate(record.periodEnd)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm">
                          <span className="text-pink-200/80">
                            Flux: {getFlowLabel(record.flowIntensity)}
                          </span>
                          {(record.cycleLength || record.calculatedCycleLength) && (
                            <span className="text-pink-200/80">
                              Cycle: {record.cycleLength || record.calculatedCycleLength} jours
                            </span>
                          )}
                        </div>
                        {record.notes && (
                          <p className="text-pink-200/60 text-xs mt-1">{record.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setEditingRecord(record.id)}
                          className="text-pink-300 hover:bg-white/10 h-8 w-8 p-0"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleDeleteRecord(record.id)}
                          className="text-red-400 hover:bg-red-500/20 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Tips */}
        <Card className="p-4 bg-blue-500/20 border-blue-400/30">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-300 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-white font-medium text-sm">Conseil</h4>
              <p className="text-blue-200 text-xs mt-1">
                Plus vous enregistrez de cycles, plus les prédictions seront précises. 
                Essayez d'enregistrer chaque période avec la date de début et de fin.
              </p>
            </div>
          </div>
        </Card>
      </div>

      <BottomNav active="calendar" />
    </div>
  );
};

export default CycleHistory;
